import { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { SquaresFour, HardHat, Kanban, UsersThree, SignOut, Globe, GearSix, Shield, ShieldCheck, UsersFour, Briefcase, Hammer, Notebook, Package, CurrencyDollar, ChartLineUp, Wrench, List, X, CloudArrowUp, CaretDoubleLeft, CaretDoubleRight, House, ClockCounterClockwise } from "@phosphor-icons/react";
import { useLang } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { usePermissions } from "@/lib/permissions";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import PWAControls from "@/components/PWAControls";
import NotificationsBell from "@/components/NotificationsBell";
import KioskToggle from "@/components/KioskToggle";
import ShiftOwnerChip from "@/components/ShiftOwnerChip";
import OpsProjectSelector from "@/components/OpsProjectSelector";
import { initOfflineSync, subscribePending, flushQueue } from "@/lib/offlineQueue";

export default function Layout({ children }) {
  const { t, lang, switchLang } = useLang();
  const { user, logout } = useAuth();
  const { has } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const [logo, setLogo] = useState(null);
  const [invLow, setInvLow] = useState(0);
  const [hrPending, setHrPending] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopCollapsed, setDesktopCollapsed] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage?.getItem("sidebar-collapsed") === "1";
  });
  const [kiosk, setKiosk] = useState(() => {
    if (typeof window === "undefined") return false;
    return !!document.fullscreenElement;
  });
  const [pendingOffline, setPendingOffline] = useState(0);
  const [isOffline, setIsOffline] = useState(typeof navigator !== "undefined" ? !navigator.onLine : false);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Escuchar cambios de kiosk emitidos por KioskToggle
  useEffect(() => {
    const onKiosk = (e) => setKiosk(!!e.detail?.active);
    window.addEventListener("kiosk-toggle", onKiosk);
    return () => window.removeEventListener("kiosk-toggle", onKiosk);
  }, []);

  const toggleDesktop = () => {
    setDesktopCollapsed((c) => {
      const next = !c;
      try { window.localStorage?.setItem("sidebar-collapsed", next ? "1" : "0"); } catch (_e) { /* noop */ }
      return next;
    });
  };

  // Sincroniza la cola offline: intenta subir turnos guardados en IndexedDB
  // al recuperar conexión + poll cada 60s. Suscribe un badge visual con el
  // conteo pendiente.
  useEffect(() => {
    initOfflineSync(api);
    const unsub = subscribePending(setPendingOffline);
    return unsub;
  }, []);

  // Track connectivity for the top offline banner. The banner covers both:
  // (a) users who logged in online and later lost signal, and
  // (b) users who resumed a cached session offline (user._offline === true).
  useEffect(() => {
    const on = () => setIsOffline(false);
    const off = () => setIsOffline(true);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  const [brand, setBrand] = useState({ title: "UG Managment", byline: "by OOMINING", tagline: "" });
  useEffect(() => {
    const fetchCompany = () => api.get("/settings/company").then((r) => {
      setLogo(r.data.logo);
      setBrand({
        title: r.data.sidebar_title || "UG Managment",
        byline: r.data.sidebar_byline || "by OOMINING",
        tagline: r.data.sidebar_tagline || "",
      });
    }).catch(() => {});
    fetchCompany();
    window.addEventListener("company-logo-updated", fetchCompany);
    return () => window.removeEventListener("company-logo-updated", fetchCompany);
  }, []);

  useEffect(() => {
    if (!has("inventory.view")) return;
    let alive = true;
    const load = () => api.get("/inventory/summary")
      .then((r) => alive && setInvLow(r.data?.below_min_count || 0))
      .catch(() => {});
    load();
    const t = setInterval(load, 60_000);   // refresh every minute
    window.addEventListener("inventory-updated", load);
    return () => { alive = false; clearInterval(t); window.removeEventListener("inventory-updated", load); };
  }, [has]);

  // Sidebar badge for pending HR requests (admin/manager/supervisor only)
  useEffect(() => {
    if (!["admin", "manager", "jefatura", "supervisor"].includes(user?.role)) return;
    let alive = true;
    const load = () => api.get("/hr/requests/pending-count")
      .then((r) => alive && setHrPending(r.data?.count || 0))
      .catch(() => {});
    load();
    const t = setInterval(load, 60_000);
    window.addEventListener("hr-requests-updated", load);
    return () => { alive = false; clearInterval(t); window.removeEventListener("hr-requests-updated", load); };
  }, [user?.role]);

  const allNav = [
    { to: "/", label: t("dashboard"), icon: SquaresFour, testid: "nav-dashboard", perm: "dashboard.view" },
    { to: "/me", label: t("myWork"), icon: Briefcase, testid: "nav-my-work", perm: "mywork.view" },
    { to: "/projects", label: t("projects"), icon: Kanban, testid: "nav-projects", perm: "projects.view" },
    { to: "/operations", label: t("operations"), icon: Hammer, testid: "nav-operations", perm: "operations.view" },
    { to: "/bitacora", label: t("bitacora"), icon: Notebook, testid: "nav-bitacora", perm: "bitacora.view" },
    { to: "/inventory", label: t("inventory"), icon: Package, testid: "nav-inventory", perm: "inventory.view" },
    { to: "/finance", label: t("finance"), icon: CurrencyDollar, testid: "nav-finance", perm: "finance.view" },
    { to: "/maintenance", label: t("maintenance"), icon: Wrench, testid: "nav-maintenance", perm: "maintenance.view" },
    { to: "/camp", label: t("camp"), icon: House, testid: "nav-camp", perm: "camp.view" },
    { to: "/hse", label: t("hse"), icon: Shield, testid: "nav-hse", perm: "hse.view" },
    { to: "/hr", label: t("hr"), icon: UsersFour, testid: "nav-hr", perm: "hr.view" },
    { to: "/users", label: t("users"), icon: UsersThree, testid: "nav-users", perm: "users.manage" },
    { to: "/settings", label: t("settings"), icon: GearSix, testid: "nav-settings", perm: "settings.view" },
    { to: "/admin/security", label: "Ciberseguridad", icon: ShieldCheck, testid: "nav-security", perm: "users.manage" },
    { to: "/admin/audit-history", label: "Historial de capturas", icon: ClockCounterClockwise, testid: "nav-audit-history", perm: "security.audit" },
  ];
  const nav = allNav.filter((item) => has(item.perm));

  return (
    <div className="flex min-h-screen bg-slate-50">
      {/* Backdrop para móvil cuando el sidebar está abierto */}
      {mobileOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-30 md:hidden"
          data-testid="sidebar-backdrop"
        />
      )}

      <aside
        data-testid="sidebar"
        aria-hidden={(!mobileOpen && desktopCollapsed) || kiosk ? "true" : "false"}
        className={`fixed inset-y-0 left-0 w-60 border-r border-slate-200 bg-white flex flex-col z-40
                    transition-transform duration-200 ease-out
                    ${mobileOpen && !kiosk ? "translate-x-0" : "-translate-x-full"}
                    ${desktopCollapsed || kiosk ? "md:-translate-x-full" : "md:translate-x-0"}
                    md:z-20`}
      >
        <div className="px-6 py-6 border-b border-slate-200 flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            {logo && <img src={logo} alt="logo" data-testid="sidebar-company-logo" className="max-h-10 max-w-40 object-contain mb-3" />}
            <h1 data-testid="sidebar-brand-title"
                className="font-heading font-black text-lg tracking-tight text-slate-900 leading-tight">
              {(() => {
                const parts = brand.title.trim().split(/\s+/);
                if (parts.length < 2) return brand.title;
                return <>{parts[0]} <span className="text-blue-900">{parts.slice(1).join(" ")}</span></>;
              })()}
            </h1>
            <p data-testid="sidebar-brand-byline"
               className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
              {brand.byline}
            </p>
            <p data-testid="sidebar-brand-tagline" className="overline-label mt-1">
              {brand.tagline || t("tagline")}
            </p>
          </div>
          {/* Cerrar sidebar en móvil */}
          <button
            onClick={() => setMobileOpen(false)}
            className="md:hidden -mr-2 -mt-2 p-2 text-slate-500 hover:text-slate-900"
            data-testid="sidebar-close"
            aria-label="Cerrar menú"
          >
            <X size={18} weight="bold" />
          </button>
        </div>
        <nav className="flex-1 py-4 overflow-y-auto">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-6 py-3.5 text-sm font-medium border-l-2 transition-colors touch-manipulation active:bg-slate-100 ${
                  isActive
                    ? "border-blue-900 bg-blue-50 text-blue-900"
                    : "border-transparent text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                }`
              }
            >
              <item.icon size={20} weight="bold" /> {item.label}
              {item.to === "/inventory" && invLow > 0 && (
                <span data-testid="nav-inventory-badge" className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-bold rounded-full bg-red-600 text-white">
                  {invLow}
                </span>
              )}
              {item.to === "/hr" && hrPending > 0 && (
                <span
                  data-testid="nav-hr-badge"
                  title={`${hrPending} solicitud(es) pendiente(s) de revisión`}
                  className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-bold rounded-full bg-red-600 text-white animate-pulse"
                >
                  {hrPending}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-slate-200 p-4 space-y-3">
          <div className="flex items-center gap-2 px-2">
            <div className="h-8 w-8 rounded-md bg-blue-900 text-white flex items-center justify-center text-xs font-bold">
              {user?.name?.[0]?.toUpperCase()}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-900 truncate" data-testid="current-user-name">{user?.name}</p>
              <p className="text-xs text-slate-500 uppercase tracking-wider">{user?.role === "manager" ? t("managerRole") : t(user?.role)}</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="w-full justify-start gap-2"
            data-testid="logout-button"
            onClick={async () => { await logout(); navigate("/login"); }}
          >
            <SignOut size={16} /> {t("logout")}
          </Button>
        </div>
      </aside>
      <div className={`flex-1 transition-[margin] duration-200 ease-out ${desktopCollapsed || kiosk ? "md:ml-0" : "md:ml-60"}`}>
        <header className="sticky top-0 z-10 h-14 border-b border-slate-200 bg-white/80 backdrop-blur-md flex items-center px-4 md:px-8 gap-2">
          {/* Hamburger móvil (oculto en kiosko) */}
          {!kiosk && (
          <button
            onClick={() => setMobileOpen(true)}
            className="md:hidden p-2 -ml-2 text-slate-700 hover:text-blue-900 active:scale-95 transition-transform touch-manipulation"
            data-testid="sidebar-open"
            aria-label="Abrir menú"
          >
            <List size={24} weight="bold" />
          </button>
          )}
          {/* Toggle sidebar desktop (oculto en kiosko) */}
          {!kiosk && (
          <button
            onClick={toggleDesktop}
            className="hidden md:inline-flex items-center gap-1.5 px-2.5 py-1.5 -ml-2 rounded-md text-slate-600 hover:bg-slate-100 hover:text-blue-900 transition-colors text-xs font-semibold"
            data-testid="sidebar-toggle-desktop"
            aria-label={desktopCollapsed ? t("expandMenu") : t("hideMenu")}
            title={desktopCollapsed ? t("expandMenu") : t("hideMenu")}
          >
            {desktopCollapsed ? <CaretDoubleRight size={16} weight="bold" /> : <CaretDoubleLeft size={16} weight="bold" />}
            <span className="hidden lg:inline">{desktopCollapsed ? t("menu") : t("hide")}</span>
          </button>
          )}
          <div className="flex items-center gap-2 md:gap-3 ml-auto">
            {/* Indicador siempre visible: red / cola / online */}
            <button
              onClick={() => flushQueue(api)}
              title={
                isOffline
                  ? "Sin conexión — los cambios se guardan localmente"
                  : pendingOffline > 0
                    ? `${pendingOffline} cambios en cola · click para sincronizar`
                    : "En línea · cambios sincronizando en tiempo real"
              }
              data-testid="connectivity-indicator"
              className={`flex items-center gap-1.5 px-2 py-1 rounded-full border text-xs font-bold transition-colors ${
                isOffline
                  ? "bg-red-100 border-red-200 text-red-800 hover:bg-red-200"
                  : pendingOffline > 0
                    ? "bg-amber-100 border-amber-200 text-amber-800 hover:bg-amber-200"
                    : "bg-emerald-100 border-emerald-200 text-emerald-800 hover:bg-emerald-200"
              }`}
            >
              <span className={`inline-block h-2 w-2 rounded-full ${isOffline ? "bg-red-600" : pendingOffline > 0 ? "bg-amber-500 animate-pulse" : "bg-emerald-500"}`} />
              <CloudArrowUp size={13} weight="bold" />
              <span className="hidden sm:inline">
                {isOffline ? "Sin red" : pendingOffline > 0 ? `${pendingOffline} en cola` : "En línea"}
              </span>
            </button>
            <PWAControls />
            <NotificationsBell />
            <ShiftOwnerChip />
            <OpsProjectSelector />
            <KioskToggle />
            <div className={`flex items-center gap-2 ${kiosk ? "hidden" : ""}`}>
              <Globe size={16} className="text-slate-500" />
              <button
                data-testid="lang-es-button"
                onClick={() => switchLang("es")}
                className={`text-xs font-bold px-2 py-1 rounded transition-colors ${lang === "es" ? "bg-blue-900 text-white" : "text-slate-500 hover:text-slate-900"}`}
              >ES</button>
              <button
                data-testid="lang-en-button"
                onClick={() => switchLang("en")}
                className={`text-xs font-bold px-2 py-1 rounded transition-colors ${lang === "en" ? "bg-blue-900 text-white" : "text-slate-500 hover:text-slate-900"}`}
              >EN</button>
            </div>
          </div>
        </header>
        {(isOffline || user?._offline) && (
          <div
            data-testid="offline-mode-banner"
            className="bg-amber-500 text-slate-950 border-b border-amber-600 px-4 md:px-8 py-2 flex items-center justify-center gap-2 text-xs font-black tracking-wide uppercase"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M2 8.82a15 15 0 0 1 20 0"/><path d="M5 12.859a10 10 0 0 1 14 0"/><path d="M8.5 16.429a5 5 0 0 1 7 0"/><line x1="12" y1="20" x2="12.01" y2="20"/><line x1="2" y1="2" x2="22" y2="22"/></svg>
            {t("offlineBanner")}
            <span className="hidden sm:inline text-[10px] font-semibold opacity-80 normal-case tracking-normal">
              · {t("offlineBannerHint")}
            </span>
          </div>
        )}
        <main className="p-4 md:p-8">{children}</main>
      </div>
    </div>
  );
}
